/**
 * openInDependencyViewer — hand off the current object (or a specific
 * dependent) to the existing Dependency Viewer workspace tab. Mirrors the
 * spawn pattern used by `view-dependencies` action under
 * `src/features/shared/object-actions/actions/designActions.ts`.
 *
 * Slice H3.
 */
import type { DependencyObjectType } from '../dependencies/types';
import {
  getActiveConnectionForId,
  type ActiveConnectionInfo,
} from './activeConnectionLookup';
import { useWorkspaceStore } from '../../core/store/workspaceStore';

export interface OpenInDependencyViewerArgs {
  readonly connectionId: string | null | undefined;
  readonly database: string | null | undefined;
  readonly schema: string | null | undefined;
  readonly objectName: string | null | undefined;
  readonly objectType: DependencyObjectType;
}

export type DependencyViewerHandoffResult =
  | { readonly status: 'opened'; readonly tabId: string }
  | { readonly status: 'missing-fields' }
  | { readonly status: 'connection-not-found' };

/**
 * Opens a `dependency-viewer` tab for the given object. Returns a
 * discriminated result so the caller can surface an error message when the
 * connection is missing or required fields are blank.
 *
 * Implementation note — workspaceStore lookup is done lazily so test code can
 * stub `useWorkspaceStore.getState()` without static module side-effects.
 */
export function openInDependencyViewer(
  args: OpenInDependencyViewerArgs,
  deps: {
    readonly resolveActiveConnection?: (
      connectionId: string,
    ) => ActiveConnectionInfo | null;
  } = {},
): DependencyViewerHandoffResult {
  const { connectionId, database, schema, objectName, objectType } = args;
  if (!connectionId || !database || !objectName) {
    return { status: 'missing-fields' };
  }

  const lookup = deps.resolveActiveConnection ?? getActiveConnectionForId;
  const active = lookup(connectionId);
  if (!active) return { status: 'connection-not-found' };

  const tabId = useWorkspaceStore.getState().addTab({
    title: `Dependencies: ${objectName}`,
    type: 'dependency-viewer',
    content: {
      objectName,
      objectType,
      schema: schema ?? '',
      database,
      viewMode: 'graph',
      isLoading: true,
    },
    connectionInfo: {
      connectionId,
      connectionName: active.connectionName,
      serverHost: active.serverHost,
      database,
    },
  });

  return { status: 'opened', tabId };
}
